import Stack from "@mui/material/Stack";
import { AMLData, AMLIncomeSource, type AMLFormValues } from "@repo/logic";
import { FC, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useScrollFirstError } from "../../hooks";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import { FormError } from "../Form/FormError";
import { RadioGroup } from "../Form/RadioGroup";
import { SelectMenuItem } from "../Form/SelectInput";
import * as Styled from "./AMLQuestionsForm.styled";
import { getIncomeSourceInfo } from "./helpers";

type AMLQuestionsFormProps = {
    autoScrollFirstError?: boolean;
    helpText?: string;
};

export const AMLQuestionsForm: FC<AMLQuestionsFormProps> = ({ autoScrollFirstError = true, helpText }) => {
    const { t } = useTypedTranslation();
    const isLabelClickable = useIsDesktop();
    const { watch, setValue, formState, getFieldState, trigger } = useFormContext<AMLFormValues>();
    const containerRef = useRef<HTMLDivElement | null>(null);
    useScrollFirstError<AMLFormValues>(
        { formState },
        {
            behavior: "smooth",
            root: containerRef.current ?? undefined,
            topOffset: 100,
        },
        { auto: autoScrollFirstError },
    );

    const { businessIncomeId, employmentType, otherIncomeId, taxResidence, propertySources } = watch();

    const incomeSourceInfo = useMemo(
        () =>
            getIncomeSourceInfo({
                employmentType,
                businessIncomeId,
                otherIncomeId,
            }),
        [employmentType, businessIncomeId, otherIncomeId],
    );
    const specifyIncome = incomeSourceInfo?.InfoRequired && employmentType;

    return (
        <Styled.ContainerStack ref={containerRef}>
            {/* Optional greeting + help text (used by drawer flows) */}
            {helpText && (
                <Styled.FormGroup>
                    <Styled.QuestionHeader>{t("AML.amlReasoning.greeting")}</Styled.QuestionHeader>
                    <Styled.AmlBasicLabel>{helpText}</Styled.AmlBasicLabel>
                </Styled.FormGroup>
            )}

            {/* Instructions for checkboxes */}
            <Styled.FormGroup>
                {!helpText && (
                    <Styled.QuestionHeader>{t("AML.amlQuestionaire.optionsInstruction")}</Styled.QuestionHeader>
                )}
                <Styled.AmlCheckBoxInput
                    LabelTextComponent={Styled.AmlBasicLabel}
                    name="usaResident"
                    label={t("AML.amlQuestionaire.usaResident")}
                    labelClickable={isLabelClickable}
                />
                <Styled.AmlCheckBoxInput
                    LabelTextComponent={Styled.AmlBasicLabel}
                    name="greenCardHolder"
                    label={t("AML.amlQuestionaire.greenCardHolder")}
                    labelClickable={isLabelClickable}
                />
                <Styled.AmlCheckBoxInput
                    LabelTextComponent={Styled.AmlBasicLabel}
                    name="politicallyExposed"
                    label={t("AML.amlQuestionaire.politicallyExposed")}
                    labelClickable={isLabelClickable}
                />
            </Styled.FormGroup>

            {/* Tax residence select */}
            <Styled.FormGroup>
                <Styled.AmlSelectInput
                    LabelComponent={Styled.QuestionHeader}
                    name="taxResidence"
                    label={t("AML.amlQuestionaire.taxResidence")}
                    isRequired
                >
                    {AMLData.Countries.map((country) => (
                        <SelectMenuItem key={country.country_code_a3} value={country.country_code_a3}>
                            {country.country_name_cz}
                        </SelectMenuItem>
                    ))}
                </Styled.AmlSelectInput>

                {/* Tax number input */}
                {taxResidence !== "CZE" && (
                    <Styled.AmlTextInput
                        LabelComponent={Styled.QuestionHeader}
                        label={t("AML.amlQuestionaire.taxNumber")}
                        name="taxNumber"
                        isRequired
                    />
                )}
            </Styled.FormGroup>

            {/* Employment type radio buttons */}
            <Styled.FormGroup>
                <Styled.QuestionHeader>{t("AML.amlQuestionaire.incomeSource.label")}</Styled.QuestionHeader>
                <RadioGroup name="employmentType">
                    <Styled.AmlRadioInput
                        LabelTextComponent={Styled.AmlBasicLabel}
                        name="employmentType"
                        value={AMLIncomeSource.Employment}
                        id="employment"
                        label={t("AML.amlQuestionaire.incomeSource.employment")}
                        labelClickable={isLabelClickable}
                    />
                    <Styled.AmlRadioInput
                        LabelTextComponent={Styled.AmlBasicLabel}
                        name="employmentType"
                        value={AMLIncomeSource.Business}
                        id="business"
                        label={t("AML.amlQuestionaire.incomeSource.business")}
                        labelClickable={isLabelClickable}
                    />
                    <Styled.AmlRadioInput
                        LabelTextComponent={Styled.AmlBasicLabel}
                        name="employmentType"
                        value={AMLIncomeSource.Other}
                        id="other"
                        label={t("AML.amlQuestionaire.incomeSource.other")}
                        labelClickable={isLabelClickable}
                    />
                </RadioGroup>
                {/* Business income select */}
                {employmentType === AMLIncomeSource.Business && (
                    <Styled.AmlSelectInput
                        LabelComponent={Styled.QuestionHeader}
                        name="businessIncomeId"
                        label={t("AML.amlQuestionaire.incomeSource.businessArea")}
                        isRequired
                    >
                        <SelectMenuItem value="">{t("global.pleaseSelectAnOption")}</SelectMenuItem>
                        {AMLData.BusinessIncome.map((businessType) => (
                            <SelectMenuItem key={businessType.Id} value={businessType.Job}>
                                {businessType.Job}
                            </SelectMenuItem>
                        ))}
                    </Styled.AmlSelectInput>
                )}
                {/* Other income select */}
                {employmentType === AMLIncomeSource.Other && (
                    <Styled.AmlSelectInput
                        LabelComponent={Styled.QuestionHeader}
                        name="otherIncomeId"
                        label={t("AML.amlQuestionaire.incomeSource.specifiyIncomeSource")}
                        isRequired
                    >
                        <SelectMenuItem value="">{t("global.pleaseSelectAnOption")}</SelectMenuItem>
                        {AMLData.OtherIncome.map((otherIncome) => (
                            <SelectMenuItem key={otherIncome.Id} value={otherIncome.Job}>
                                {otherIncome.Job}
                            </SelectMenuItem>
                        ))}
                    </Styled.AmlSelectInput>
                )}
                {/* Specify income source input */}
                {specifyIncome && (
                    <Styled.AmlTextInput
                        LabelComponent={Styled.QuestionHeader}
                        label={t("AML.amlQuestionaire.specify")}
                        name="incomeSourceSpecification"
                        isRequired
                    />
                )}
            </Styled.FormGroup>

            {/* Property source checkboxes */}
            <Styled.FormGroup>
                <Styled.QuestionHeader>{t("AML.amlQuestionaire.propertySource.label")}</Styled.QuestionHeader>
                <Stack>
                    {AMLData.SourceOfProperty.map((propertySource) => (
                        <Styled.AmlCheckBoxInput
                            LabelTextComponent={Styled.AmlBasicLabel}
                            key={propertySource.Id}
                            name={`propertySources.${propertySource.Id}`}
                            label={propertySource.Name}
                            labelClickable={isLabelClickable}
                            checked={propertySources?.[propertySource.Id] || false}
                            onChange={(_, checked: boolean) => {
                                setValue("propertySources", {
                                    ...propertySources,
                                    [propertySource.Id]: checked,
                                });
                                // ensure Yup object-level test runs immediately and RHF clears root error
                                void trigger("propertySources");
                            }}
                        />
                    ))}
                    {formState.errors.propertySources && (
                        <FormError
                            error={getFieldState("propertySources", formState).error?.root}
                            fieldName={"propertySources"}
                        />
                    )}
                    {propertySources?.[8] && (
                        <Styled.AmlTextInput
                            LabelComponent={Styled.QuestionHeader}
                            label={t("AML.amlQuestionaire.specifyPropertySource")}
                            name="propertySourceSpecification"
                            isRequired
                        />
                    )}
                </Stack>
            </Styled.FormGroup>

            {/* Average income radio buttons */}
            <Styled.FormGroup>
                <Styled.QuestionHeader>{t("AML.amlQuestionaire.averageIncome.label")}</Styled.QuestionHeader>
                <RadioGroup name="averageIncome">
                    {AMLData.AverageIncome.map((avgIncome) => (
                        <Styled.AmlRadioInput
                            LabelTextComponent={Styled.AmlBasicLabel}
                            key={avgIncome.Id}
                            name="averageIncome"
                            value={avgIncome.Name}
                            id={`averageIncome-${avgIncome.Id}`}
                            label={avgIncome.Name}
                            labelClickable={isLabelClickable}
                            isRequired
                        />
                    ))}
                </RadioGroup>
            </Styled.FormGroup>
        </Styled.ContainerStack>
    );
};
